import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { whatsappNumber, brandAssets } from '../data/mockData';
import { useLanguage } from '../context/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';
import ThemeToggle from './ThemeToggle';

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { language } = useLanguage();
  const t = language === 'fr';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Navigation simplifiée - Focus Abidjan
  const mainNavLinks = [
    { path: '/', label: t ? 'Accueil' : 'Home' },
    { path: '/produits', label: t ? 'Produits' : 'Products' },
    { path: '/tracabilite', label: t ? 'Traçabilité' : 'Traceability' },
    { path: '/diallo', label: t ? 'Réseau Diallo' : 'Diallo Network' },
    { path: '/b2b', label: t ? 'Professionnels' : 'Business' },
    { path: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Top bar */}
      <div className="bg-[#1A5C38] text-white py-2 hidden md:block">
        <div className="container flex justify-between items-center">
          <div className="flex items-center gap-6 text-sm">
            <a href={`https://wa.me/${whatsappNumber}`} className="flex items-center gap-2 hover:text-[#F5A623] transition-colors">
              <Phone size={14} />
              <span>{whatsappNumber}</span>
            </a>
            <span className="text-white/40">|</span>
            <span className="text-white/70">
              {t ? 'Fruits & légumes frais à Abidjan' : 'Fresh fruits & vegetables in Abidjan'}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <ThemeToggle />
            <LanguageSwitcher />
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav 
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/95 dark:bg-[#0F1A14]/95 backdrop-blur-md shadow-md' 
            : 'bg-[#F8F2E8] dark:bg-[#0F1A14]'
        }`}
      >
        <div className="container">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center group">
              <img 
                src={brandAssets.logo} 
                alt="Océana" 
                className="h-10 md:h-12 w-auto transition-transform duration-300 group-hover:scale-105"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {mainNavLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    isActive(link.path) 
                      ? 'bg-[#1A5C38] text-white' 
                      : 'text-[#1A5C38] dark:text-white hover:bg-[#1A5C38]/10'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center gap-3">
              <ThemeToggle />
              <LanguageSwitcher />
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="p-2 text-[#1A5C38] dark:text-white hover:bg-[#1A5C38]/10 dark:hover:bg-white/10 rounded-lg transition-colors"
              >
                {isOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div 
          className={`lg:hidden absolute top-full left-0 w-full bg-white dark:bg-[#1A2B22] shadow-xl transition-all duration-300 overflow-hidden ${
            isOpen ? 'max-h-[80vh] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="container py-4">
            <div className="flex flex-col gap-1">
              {mainNavLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`px-4 py-3 rounded-lg text-base font-medium transition-all ${
                    isActive(link.path) 
                      ? 'bg-[#1A5C38] text-white' 
                      : 'text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-white/10'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};
